import React, { useState } from 'react';
import { MDXProvider, MDXProviderComponentsProp } from '@mdx-js/react';
import { withPrefix } from 'gatsby';
// @ts-ignore
import { preToCodeBlock } from 'mdx-utils';
import { Code, CodeBlock } from './Code';
import { H2, H3, HeadingProps } from './Headings';
import { Link, LinkProps } from './Link';
import { Aside, AsideProps } from './Aside';
import { Pagination } from './Pagination';
import { ContentMenu } from './ContentMenu';

interface MDXTemplateProps {
  children: React.ReactNode;
  path: string;
}

/**
 * A helper function that converts Markdown elements into custom components
 */
const components = {
  // Custom code blocks with syntax highlighting
  pre: (preProps: React.HTMLAttributes<HTMLPreElement>) => {
    const props = preToCodeBlock(preProps);
    if (props) {
      return <Code {...props} />;
    } else {
      return <pre {...preProps} />;
    }
  },
  table: (tableProps: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="table-wrapper">
      <table {...tableProps} />
    </div>
  ),
  a: (anchorProps: LinkProps) => <Link {...anchorProps} />,
  img: ({
    src,
    alt,
    ...imgProps
  }: React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >) => <img {...imgProps} src={withPrefix(src ?? '')} alt={alt} />,
  aside: (asideProps: AsideProps) => <Aside {...asideProps} />,
  CodeBlock,
  h2: (h2Props: HeadingProps) => <H2 {...h2Props} />,
  h3: (h3Props: HeadingProps) => <H3 {...h3Props} />,
};

/**
 * A custom layout for MDX pages only, powered by the @mdx-js/react MDXProvider
 * and reusable components for markdown.
 */
export function MDXTemplate({ path, children }: MDXTemplateProps) {
  // Only render the ContentMenu if there are more than two <h2> or <h3> tags
  const [headingCount, setHeadingCount] = useState<null | number>(null);
  return (
    <MDXProvider
      components={components as unknown as MDXProviderComponentsProp}>
      <div
        id="docs-body"
        className={headingCount && headingCount > 2 ? 'grid' : undefined}
        style={headingCount === null ? { visibility: 'hidden' } : undefined}>
        <div className="markdown" style={{ overflow: 'hidden' }}>
          {children}
          {path ? <Pagination path={path} pageTitle /> : null}
        </div>
        <div className="page-contents">
          <ContentMenu
            pathname={path}
            headings={['h2', 'h3']}
            headingCount={setHeadingCount}
          />
        </div>
      </div>
    </MDXProvider>
  );
}
